import { array, boolean, number, object, string } from "yup";
import { Data, dateMax, stringMax } from "./utils";
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_REGEX,
} from "../utils/constants/validation";

export class ProfileStatus {
  static Actif = new ProfileStatus(1);
  static Archive = new ProfileStatus(2);

  constructor(status) {
    this.status = status;
  }
}

export class ProfileInviteStatus {
  static INVITED = "invited";
  static CONNECTED = "connected";
}

export class AncienneteStatus {
  static MONTH3 = "3mois";
  static MONTH6 = "6mois";
  static YEAR1 = "1an";
  static YEAR2 = "2ans";
  static YEAR2PLUS = "2ans+";
}

export class StatusFiscal {
  static NUMICRO = "nu-micro";
  static NUREEL = "nu-reel";
  static LMNPMICRO = "lmnp-micro";
  static LMNPREEL = "lmnp-reel";
  static IS = "is";
}

export const StatusFiscalDescription = {
  [StatusFiscal.NUMICRO]: "Revenus fonciers - régime micro foncier",
  [StatusFiscal.NUREEL]: "Revenus fonciers - régime réel",
  [StatusFiscal.LMNPMICRO]: "LMNP ou LMP - régime micro-BIC",
  [StatusFiscal.LMNPREEL]: "LMNP ou LMP - régime réel",
  [StatusFiscal.IS]: "Société soumise à l'impôt sur les sociétés",
};

export class StatusPro {
  static ETUDIANT = "Etudiant";
  static CDI = "CDI";
  static CDD = "CDD";
  static INTERIM = "Interimaire";
  static FONCTIONNAIRE = "Fonctionnaire";
  static INDEPENDANT = "Travailleur indépendant";
  static RETRAITE = "Retraité";
  static SANSEMPLOI = "Sans emploi";
}
export class TypeSociete {
  static SAS = "SAS";
  static SA = "SA";
  static SASU = "SASU";
  static SCA = "SCA";
  static SCI = "SCI";
  static SARL = "SARL";
  static SARL_FAMILLE = "SARL de famille";
  static SNC = "SNC";
  static ASSOCIATION = "Association";
  static EURL = "EURL";
  static ENTREPRISE_INDIVIDUELLE = "Entreprise individuelle";
  static AUTRE = "Autre";
}

export class Profile extends Data {
  id = undefined;
  id_profile_bailleur = undefined;
  files = undefined;
  id_mandataire = undefined;
  nom = undefined;
  prenom = undefined;
  nom_societe = undefined;
  siret = undefined;
  numero_tva = undefined;
  type = undefined;
  id_compte = undefined;
  adresse = undefined;
  cp = undefined;
  complement_adresse = undefined;
  ville = undefined;
  pays = undefined;
  tel = undefined;
  socType = undefined;
  birthday = undefined;
  lieu_naissance = undefined;
  sci_famille = undefined;
  etudiant = undefined;
  activite = undefined;
  revenus_brut = undefined;
  mail = undefined;
  garantor_of = undefined;
  garantors = [];
  legal_representative_of = undefined;
  legal_representatives = [];
  nationalite = undefined;
  profile_type = undefined;
  caution_type = undefined;
  status_pro = undefined;
  contract_number = undefined;
  loyer_max_visale = undefined;
  validite_visale = undefined;
  status = undefined;
  date_arrivee = undefined;
  date_depart = undefined;
  anciennete = undefined;
  presentation = undefined;
  status_invite = undefined;
  old_ville = undefined;
  old_cp = undefined;
  old_complement_adresse = undefined;
  old_adresse = undefined;
}

export function getProfileBaseSchema(withMandatoryFields) {
  return object({
    type: number().integer().required("Un type de personne est obligatoire"),
    mail: stringMax(250)
      .matches(EMAIL_REGEX, EMAIL_INVALID_MESSAGE)
      .required("L'adresse e-mail est obligatoire "),
    prenom: stringMax(250).required("Le prénom est obligatoire"),
    nom: stringMax(250).required("Le nom est obligatoire"),
    nom_societe: stringMax(250).when("type", {
      is: 2,
      then: (schema) => schema.required("Le nom de la société est obligatoire"),
      otherwise: (schema) => schema.notRequired(),
    }),
    siret: stringMax(250).optional(" "),
    numero_tva: stringMax(20).optional(" "),
    socType: string().when("type", {
      is: 2,
      then: (schema) => schema.required("Le type de société est obligatoire"),
      otherwise: (schema) => schema.notRequired(),
    }),
    pays: stringMax(250).required("Le pays est obligatoire"),
    adresse: stringMax(250).required("L'adresse est obligatoire"),
    cp: stringMax(10).required("Le code postal est obligatoire"),
    complement_adresse: stringMax(37).notRequired(""),
    ville: stringMax(250).required("La ville est obligatoire"),
    old_adresse: stringMax(250).notRequired(),
    old_cp: stringMax(10).notRequired(),
    old_complement_adresse: stringMax(37).notRequired(),
    old_ville: stringMax(250).notRequired(),
    tel: withMandatoryFields
      ? stringMax(20).required("Le numéro de téléphone est obligatoire")
      : stringMax(20).notRequired(),
  });
}

export const mandatairesFields = object({
  remuneration_type: stringMax(250).required(
    "Le type de rémuneration est obligatoire",
  ),
  remuneration_montant: number().required(
    "Le montant de la rémunération est obligatoire",
  ),
});

export function getMandataireProfileSchema(withMandatoryFields) {
  return mandatairesFields.concat(
    object({
      carte_g: stringMax(250).required("Le numéro de carte G est obligatoire"),
      profile: getProfileBaseSchema(withMandatoryFields),
      carte_delivree_par: stringMax(250).required(
        "L'organisme de délivrance de la carte G est obligatoire",
      ),
      carte_delivree_date: dateMax().required(
        "La date de délivrance de la carte est obligatoire",
      ),
      carte_mentions: array()
        .of(string())
        .required("Les mentions de la carte sont obligatoires"),
      nom_garant: stringMax(250),
      adresse_garant: stringMax(250),
      montant_garanti: number().integer(),
      tva: boolean(),
      iban: stringMax(34).optional(" "),
      bic: stringMax(11).optional(" "),
    }),
  );
}

export function getBailleurProfileSchema(withMandatoryFields) {
  return getProfileBaseSchema(withMandatoryFields).concat(
    object({
      sci_famille: boolean(),
      status_pro: stringMax(250),
      birthday: withMandatoryFields
        ? dateMax().when("type", {
            is: 1,
            then: (schema) =>
              schema.required("La date de naissance est obligatoire"),
            otherwise: (schema) => schema.notRequired(),
          })
        : dateMax().notRequired(),
      lieu_naissance: withMandatoryFields
        ? stringMax(250).when("type", {
            is: 1,
            then: (schema) =>
              schema.required("Le lieu de naissance est obligatoire"),
            otherwise: (schema) => schema.notRequired(),
          })
        : string().notRequired(),
      nationalite: withMandatoryFields
        ? stringMax(250).when("type", {
            is: 1,
            then: (schema) => schema.required("La nationalité est obligatoire"),
            otherwise: (schema) => schema.notRequired(),
          })
        : stringMax(250).notRequired(),
    }),
  );
}

export function getTenantProfileSchema(withMandatoryFields) {
  return getBailleurProfileSchema(withMandatoryFields).concat(
    object({
      caution_type: stringMax(250).notRequired(),
      validite_visale: dateMax().notRequired(),
      loyer_max_visale: number().notRequired(),
      anciennete: stringMax(250).notRequired(),
      activite: stringMax(250).notRequired(),
      presentation: stringMax(2000).notRequired(),
      revenus_brut: number()
        .integer("Les revenus doivent être un nombre entier")
        .min(0, "Les revenus doivent être positifs")
        .notRequired(),
    }),
  );
}

export const PROFILE_INITIAL_VALUE = {
  type: "1",
  mail: "",
  prenom: "",
  nom: "",
  nom_societe: "",
  siret: "",
  numero_tva: "",
  pays: "France",
  complement_adresse: "",
  old_complement_adresse: "",
  adresse: "",
  old_adresse: "",
  cp: "",
  old_cp: "",
  ville: "",
  old_ville: "",
  socType: "",
  sci_famille: false,
  activite: "",
  revenus_brut: "",
  birthday: "",
  lieu_naissance: "",
  tel: "",
  nationalite: "",
  garantors: [],
  legal_representatives: [],
  files: [],
  caution_type: "",
  validite_visale: "",
  status_pro: "",
  contract_number: "",
  loyer_max_visale: "0",
  profiles: [],
  remuneration_type: "",
  remuneration_montant: "",
  anciennete: "",
  presentation: "",
};

export const MANDATAIRE_INITIAL_VALUE = {
  carte_g: "",
  remuneration_type: "",
  remuneration_montant: "",
  profile: { ...PROFILE_INITIAL_VALUE, type: "2" },
  carte_delivree_par: "",
  carte_delivree_date: "",
  carte_mentions: [],
  nom_garant: "",
  adresse_garant: "",
  montant_garanti: "",
  tva: false,
  iban: "",
  bic: "",
};

export const PROFILE_BAILLEUR_INITIAL_VALUE = {
  nom_profile: "",
  profiles: [PROFILE_INITIAL_VALUE],
  remuneration_type: "",
  remuneration_montant: "",
  notes: "",
  cc_mail: "",
  regime_fiscal: "",
  debut_activite: "",
  siret: "",
  numero_tva: "",
  sci_famille: false,
  is_societe: false,
  ics_id: "",
  iban: "",
  iban_titulaire: "",
  bic: "",
};

export const profileBailleurSchema = object({
  nom_profile: stringMax(250).required(" "),
  notes: string().optional(" "),
  cc_mail: string().optional(" "),
  regime_fiscal: string().optional(" "),
  debut_activite: dateMax().optional(" "),
  siret: stringMax(14)
    .matches(/^\d{14}$/, "Le SIRET doit contenir exactement 14 chiffres")
    .optional(" "),
  numero_tva: stringMax(20).optional(" "),
  sci_famille: boolean(),
  is_societe: boolean(),
  profiles: array().of(getBailleurProfileSchema(false)),
  ics_id: stringMax(250).optional(" "),
  iban: stringMax(250).optional(" "),
  iban_titulaire: stringMax(250).optional(" "),
  bic: stringMax(250).optional(" "),
});

export const profileMandataireSchema =
  profileBailleurSchema.concat(mandatairesFields);
